import React, { useState } from 'react';
import { Form, Input, Button, Card, Alert, Checkbox, Typography } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons';
import { authAPI, LoginData } from '../services/api';

const { Title, Text } = Typography;

interface LoginPageProps {
  onLogin: (user: any) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentView, setCurrentView] = useState<'login' | 'reset'>('login');

  const handleSubmit = async (values: LoginData & { remember?: boolean }) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await authAPI.login(values);
      
      if (response.success && response.user) {
        // "Məni xatırla" seçilmişsə, token-ləri daha uzun müddətə saxla
        if (values.remember) {
          // 30 gün üçün saxla
          const expiryDate = new Date();
          expiryDate.setDate(expiryDate.getDate() + 30);
          localStorage.setItem('remember_me', 'true');
          localStorage.setItem('token_expiry', expiryDate.toISOString());
        } else {
          // Normal müddət (7 gün - JWT refresh token müddəti)
          localStorage.removeItem('remember_me');
          localStorage.removeItem('token_expiry');
        }
        
        onLogin(response.user);
      } else {
        setError(response.errors?.email?.[0] || response.errors?.password?.[0] || response.errors?.non_field_errors?.[0] || 'Giriş xətası');
      }
    } catch (error) {
      setError('Server ilə əlaqə xətası');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (values: { email: string; new_password: string; confirm_password: string }) => {
    setIsLoading(true);
    setError('');

    try {
      // Əvvəlcə email-in mövcudluğunu yoxla
      const checkResponse = await authAPI.requestPasswordReset(values.email);
      
      if (!checkResponse.success) {
        setError(checkResponse.errors?.email?.[0] || checkResponse.message || 'Email tapılmadı');
        setIsLoading(false);
        return;
      }

      // Email mövcuddursa, birbaşa şifrəni dəyişdir
      // Bu üçün backend-də yeni endpoint lazımdır
      const resetResponse = await authAPI.directPasswordReset(values.email, values.new_password, values.confirm_password);
      
      if (resetResponse.success) {
        // Uğurlu mesaj göstər və login formuna qayıt
        setError('');
        form.resetFields();
        setCurrentView('login');
        // Uğur mesajını göstər
        setTimeout(() => {
          alert('Şifrəniz uğurla dəyişdirildi! İndi yeni şifrə ilə daxil ola bilərsiniz.');
        }, 100);
      } else {
        setError(resetResponse.errors?.new_password?.[0] || resetResponse.message || 'Şifrə dəyişdirilmədi');
      }
    } catch (error) {
      setError('Server ilə əlaqə xətası');
    } finally {
      setIsLoading(false);
    }
  };

  // const handleRegister = async (values: any) => {
  //   // Bu funksiya artıq istifadə olunmur
  // };

  if (currentView === 'reset') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card 
          className="w-full max-w-md shadow-2xl"
          style={{ borderRadius: '12px' }}
        >
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-indigo-600 rounded-full flex items-center justify-center mb-4">
              <UserOutlined className="text-2xl text-white" />
            </div>
            <Title level={2} className="mb-2">Şifrəni Dəyişdir</Title>
            <Text type="secondary">Email ünvanınızı təsdiq edin və yeni şifrə təyin edin</Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              className="mb-6"
            />
          )}

          <Form
            form={form}
            name="password-reset"
            onFinish={handlePasswordReset}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="email"
              label="Email ünvanı"
              rules={[
                { required: true, message: 'Email ünvanı tələb olunur!' },
                { type: 'email', message: 'Düzgün email ünvanı daxil edin!' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="email@example.com"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="new_password"
              label="Yeni şifrə"
              rules={[
                { required: true, message: 'Yeni şifrə tələb olunur!' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirm_password"
              label="Şifrəni təkrarla"
              dependencies={['new_password']}
              rules={[
                { required: true, message: 'Şifrə təkrarı tələb olunur!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Şifrələr uyğun gəlmir!'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                size="large"
                className="bg-indigo-600 hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700"
              >
                {isLoading ? 'Şifrə Dəyişdirilir...' : 'Şifrəni Dəyişdir'}
              </Button>
            </Form.Item>

            <Form.Item className="mb-0">
              <Button
                type="default"
                onClick={() => {
                  setCurrentView('login');
                  setError('');
                  form.resetFields();
                }}
                block
                size="large"
              >
                Geri Qayıt
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card 
        className="w-full max-w-md shadow-2xl"
        style={{ borderRadius: '12px' }}
      >
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-indigo-600 rounded-full flex items-center justify-center mb-4">
            <UserOutlined className="text-2xl text-white" />
          </div>
          <Title level={2} className="mb-2">Tələbə İdarəetmə Sistemi</Title>
          <Text type="secondary">Hesabınıza daxil olun</Text>
        </div>

        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            className="mb-6"
          />
        )}

        <Form
          form={form}
          name="login"
          onFinish={handleSubmit}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="email"
            label="Email ünvanı"
            rules={[
              { required: true, message: 'Email ünvanı tələb olunur!' },
              { type: 'email', message: 'Düzgün email ünvanı daxil edin!' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="email@example.com"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Şifrə"
            rules={[{ required: true, message: 'Şifrə tələb olunur!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item>
            <div className="flex items-center justify-between">
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>Məni xatırla</Checkbox>
              </Form.Item>
              {/* <Button 
                type="link" 
                className="p-0"
                onClick={() => setCurrentView('reset')}
              >
                Şifrəni unutmusunuz?
              </Button> */}
            </div>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              icon={<LoginOutlined />}
              block
              size="large"
              className="bg-indigo-600 hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700"
            >
              {isLoading ? 'Giriş edilir...' : 'Daxil ol'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default LoginPage;